
import { motion } from 'framer-motion'
import { X, Crown, Zap, Star, CheckCircle, AlertTriangle } from 'lucide-react'
import { UserLimits } from '@/types'
import { useTranslation } from 'react-i18next'

interface PaywallModalProps {
  isOpen: boolean
  onClose: () => void
  userLimits: UserLimits | null
  onUpgrade?: () => void
}

export default function PaywallModal({ isOpen, onClose, userLimits, onUpgrade }: PaywallModalProps) {
  if (!isOpen) return null

  const remainingPrompts = userLimits ? userLimits.maxPrompts - userLimits.promptCount : 0
  const resetDate = userLimits ? new Date(userLimits.resetDate) : new Date()
  const daysUntilReset = Math.ceil((resetDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
  const { t } = useTranslation()

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 safe-area">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative w-full max-w-md card p-6 md:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
        >
          <X className="w-5 h-5 text-white/60" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center mb-4">
            <div className="relative">
              <Crown className="w-12 h-12 text-yellow-400" />
              <div className="absolute inset-0 w-12 h-12 bg-yellow-400/20 rounded-full blur-xl animate-pulse"></div>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gradient mb-2">{t('paywall.title')}</h2>
          <p className="text-white/60">{t('paywall.description')}</p>
        </div>

        {/* Current Status */}
        <div className="bg-white/5 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-white/80 font-medium">{t('paywall.currentStatus')}</span>
            <AlertTriangle className="w-5 h-5 text-yellow-400" />
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-white/60 text-sm">{t('paywall.usedPrompts')}</span>
              <span className="text-white font-semibold">
                {userLimits?.promptCount || 0} / {userLimits?.maxPrompts || 20}
              </span>
            </div>
            
            <div className="w-full bg-white/10 rounded-full h-2">
              <div 
                className="h-2 rounded-full bg-gradient-to-r from-red-500 to-orange-500"
                style={{ 
                  width: `${userLimits ? (userLimits.promptCount / userLimits.maxPrompts) * 100 : 100}%` 
                }}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-white/60 text-sm">{t('paywall.remainingPrompts')}</span>
              <span className={`font-semibold ${remainingPrompts > 0 ? 'text-green-400' : 'text-red-400'}`}>
                {remainingPrompts}
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-white/60 text-sm">{t('paywall.resetDate')}</span>
              <span className="text-white/80 text-sm">
                {t('promptLimit.daysLeft', { days: daysUntilReset })}
              </span>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-4 mb-6">
          <h3 className="text-lg font-semibold text-white mb-3">{t('paywall.featuresTitle')}</h3>
          
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
              <span className="text-white/80 text-sm">{t('paywall.features.monthly20')}</span>
            </div>
            
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
              <span className="text-white/80 text-sm">{t('paywall.features.highQuality')}</span>
            </div>
            
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
              <span className="text-white/80 text-sm">{t('paywall.features.unlimitedRooms')}</span>
            </div>
            
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
              <span className="text-white/80 text-sm">{t('paywall.features.allModes')}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          {onUpgrade ? (
            <button
              onClick={onUpgrade}
              className="btn-primary w-full flex items-center justify-center space-x-2"
            >
              <Crown className="w-5 h-5" />
              <span>{t('paywall.upgradeNow')}</span>
            </button>
          ) : (
            <button
              disabled
              className="btn-primary w-full flex items-center justify-center space-x-2 opacity-50 cursor-not-allowed"
            >
              <Crown className="w-5 h-5" />
              <span>{t('paywall.comingSoon')}</span>
            </button>
          )}

          <button
            onClick={onClose}
            className="btn-secondary w-full flex items-center justify-center space-x-2"
          >
            <Zap className="w-5 h-5" />
            <span>{t('paywall.waitForReset')}</span>
          </button>
        </div>

        {/* Info */}
        <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
          <div className="flex items-start space-x-3">
            <Star className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-blue-300 font-medium text-sm">{t('paywall.infoTitle')}</p>
              <p className="text-blue-200/80 text-xs leading-relaxed">{t('paywall.infoBody')}</p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
